import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { from } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ProfileService } from '../services/profile.service';

/** Checks the OAuth session and loads the user profile. */
/** Checks the Supabase session and loads the profile before granting access. */
async function checkSession(
  auth: AuthService,
  router: Router,
  profiles: ProfileService,
): Promise<boolean | ReturnType<Router['createUrlTree']>> {
  if (!auth.verifyOAuthNonce()) {
    void auth.signOut();
    return router.createUrlTree(['/auth/login']);
  }
  if (!auth.session) {
    return router.createUrlTree(['/auth/login']);
  }
  await profiles.loadCurrentUser();
  const profile = profiles.currentProfile;
  if (profile && profile.isActive === false) {
    void auth.signOut();
    return router.createUrlTree(['/auth/login'], { queryParams: { auth_error: 'account_disabled' } });
  }
  return true;
}

/** Authentication guard: redirects to login if not signed in. */
/** Route guard: redirects to login if not authenticated. */
export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const profiles = inject(ProfileService);

  if (!auth.loading) {
    return checkSession(auth, router, profiles);
  }

  return auth.loading$.pipe(
    filter((loading) => !loading),
    take(1),
    switchMap(() => from(checkSession(auth, router, profiles))),
  );
};

/** Guest guard: redirects signed-in users to the home page. */
/** Route guard: redirects to home if already authenticated. */
export const guestGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.loading) {
    return auth.session ? router.createUrlTree(['/']) : true;
  }

  return auth.loading$.pipe(
    filter((loading) => !loading),
    take(1),
    map(() => (auth.session ? router.createUrlTree(['/']) : true)),
  );
};
